import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Reveals streaming text with a natural typing cadence.
 * Works with both LTR and RTL text.
 *
 * @param {string} sourceText
 * @param {{ delayMs?: number, maxBuffer?: number, enabled?: boolean, granularity?: 'word' | 'char' }} options
 *   granularity 'word' (default) reveals whole words at a time;
 *   'char' reveals one character at a time (letter-by-letter).
 * @returns {{ displayedText: string, isTyping: boolean }}
 */
export function useTypewriter(sourceText, { delayMs = 60, maxBuffer = 80, enabled = true, granularity = 'word' } = {}) {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const revealedCount = useRef(0);
  const rafId = useRef(null);
  const lastStepTime = useRef(0);
  const prevSourceRef = useRef('');

  const splitWords = useCallback((text) => {
    if (!text) return [];
    // Char mode splits into individual Unicode characters (handles Hebrew/RTL
    // and emoji correctly); word mode keeps whitespace-delimited tokens.
    if (granularity === 'char') return Array.from(text);
    return text.match(/\S+|\s+/g) || [];
  }, [granularity]);

  useEffect(() => {
    const cancelRaf = () => {
      if (rafId.current != null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };

    if (sourceText !== prevSourceRef.current && splitWords(sourceText).length < revealedCount.current) {
      revealedCount.current = 0;
      lastStepTime.current = 0;
    }
    prevSourceRef.current = sourceText || '';

    if (!enabled) {
      cancelRaf();
      lastStepTime.current = 0;
      const full = sourceText || '';
      setDisplayedText(full);
      setIsTyping(false);
      revealedCount.current = splitWords(full).length;
      return;
    }

    const allTokens = splitWords(sourceText);
    const totalTokens = allTokens.length;

    if (totalTokens === 0) {
      cancelRaf();
      lastStepTime.current = 0;
      revealedCount.current = 0;
      setDisplayedText('');
      setIsTyping(false);
      return;
    }

    const pending = totalTokens - revealedCount.current;

    if (pending <= 0) {
      setIsTyping(false);
      return;
    }

    if (pending > maxBuffer) {
      cancelRaf();
      lastStepTime.current = 0;
      revealedCount.current = totalTokens;
      setDisplayedText(sourceText);
      setIsTyping(false);
      return;
    }

    setIsTyping(true);
    cancelRaf();
    lastStepTime.current = 0;

    const step = (timestamp) => {
      if (lastStepTime.current === 0) lastStepTime.current = timestamp;
      const elapsed = timestamp - lastStepTime.current;

      if (elapsed >= delayMs) {
        lastStepTime.current = timestamp;
        revealedCount.current += 1;
        const tokens = splitWords(sourceText);
        const cap = Math.min(revealedCount.current, tokens.length);
        revealedCount.current = cap;
        const revealed = tokens.slice(0, cap).join('');
        setDisplayedText(revealed);

        if (cap >= tokens.length) {
          setIsTyping(false);
          return;
        }
      }

      rafId.current = requestAnimationFrame(step);
    };

    rafId.current = requestAnimationFrame(step);

    return () => {
      cancelRaf();
    };
  }, [sourceText, enabled, delayMs, maxBuffer, splitWords]);

  return { displayedText: enabled ? displayedText : (sourceText || ''), isTyping };
}
